import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import type { ModalAction } from '@purolator/ui-kit-angular';
import { ModalComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import {
  TERMS_AND_CONDITIONS_PDF_URL,
  TERMS_CONTENT_ASSET_URL,
} from '../review-terms/review-terms.constants';

/** One section of the Terms and Conditions modal body. */
export interface TermsContentSection {
  title: string;
  body: string;
}

/**
 * Terms and Conditions modal: scrollable legal copy, PDF download, Agree / Do not Agree.
 */
@Component({
  selector: 'app-review-terms-dialog',
  imports: [ModalComponent, TranslatePipe, TypographyComponent],
  templateUrl: './review-terms-dialog.html',
  styleUrl: './review-terms-dialog.scss',
})
export class ReviewTermsDialog {
  private readonly translation = inject(TranslationService);
  private readonly http = inject(HttpClient);

  readonly open = input(false);
  /** Shared terms checkbox control updated by Agree / Do not Agree. */
  readonly termsAccepted = input.required<FormControl<boolean>>();
  readonly agreed = output<void>();
  readonly disagreed = output<void>();

  protected readonly sections = signal<TermsContentSection[]>([]);
  protected readonly loadFailed = signal(false);

  protected readonly modalTitle = computed(() => {
    this.translation.revision();
    return this.translation.translate('createShipment.reviewAndPay.terms.modalTitle');
  });

  protected readonly termsPdfUrl = computed(() => {
    this.translation.revision();
    return this.translation.locale() === 'fr'
      ? TERMS_AND_CONDITIONS_PDF_URL.fr
      : TERMS_AND_CONDITIONS_PDF_URL.en;
  });

  protected readonly actions = computed<ModalAction[]>(() => {
    this.translation.revision();
    return [
      {
        label: this.translation.translate('createShipment.reviewAndPay.terms.disagree'),
        variant: 'secondary',
        onClick: () => this.onDisagree(),
      },
      {
        label: this.translation.translate('createShipment.reviewAndPay.terms.agree'),
        variant: 'primary',
        onClick: () => this.onAgree(),
      },
    ];
  });

  constructor() {
    effect(() => {
      if (!this.open()) {
        return;
      }
      void this.loadTermsContent();
    });
  }

  /**
   * Closes without accepting (checkbox stays unchecked).
   */
  protected onDisagree(): void {
    this.termsAccepted().setValue(false);
    this.disagreed.emit();
  }

  /**
   * Accepts terms and closes the modal.
   */
  protected onAgree(): void {
    this.termsAccepted().setValue(true);
    this.agreed.emit();
  }

  private async loadTermsContent(): Promise<void> {
    this.translation.revision();
    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    const url = TERMS_CONTENT_ASSET_URL[locale];
    this.loadFailed.set(false);

    try {
      const sections = await firstValueFrom(this.http.get<TermsContentSection[]>(url));
      this.sections.set(Array.isArray(sections) ? sections : []);
    } catch {
      this.sections.set([]);
      this.loadFailed.set(true);
    }
  }
}
